/**
 * The currencies a site may be given — the same source the Regional Settings
 * dropdowns use, so the two never disagree about which currencies the
 * platform supports. Shared by the Organisation tree's dialog and Platform
 * Config's organisation setup (CR-8227).
 */
import { useEffect, useState } from 'react';
import { preferencesAPI } from '../../lib/api';

/** Fallback list used only if the choices endpoint is unavailable. */
const FALLBACK_CURRENCIES = [
  { value: 'GBP', label: 'GBP' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
];

/**
 * Load the platform's currency choices once.
 *
 * @example
 *   const currencies = useCurrencyChoices();
 *   currencies.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>);
 * @returns the currency options, starting from a short fallback list
 */
export function useCurrencyChoices(): Array<{ value: string; label: string }> {
  const [currencies, setCurrencies] = useState<Array<{ value: string; label: string }>>(
    FALLBACK_CURRENCIES,
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await preferencesAPI.getPreferencesChoices();
        if (!cancelled && resp.data.currencies?.length) {
          setCurrencies(resp.data.currencies);
        }
      } catch {
        // Keep the fallback list — a missing choices call must not block the
        // rest of the form from saving.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return currencies;
}
